import { Popover } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { NotificationType } from '../../../app/models/Notification'
import { useStore } from '../../../app/stores/store'
import { observer } from 'mobx-react-lite'
import { useDetectClickOutside } from 'react-detect-click-outside'
import { useTranslation } from 'react-i18next'

interface Props {
  notification: NotificationType
}

function NotificationEntrys({ notification }: Props) {
  const { t } = useTranslation()
  const { notificationsStore } = useStore()
  const { handleMarkNotification, handleDeleteNotification } = notificationsStore
  const [showPopover, setShowPopover] = useState<boolean>(false)
  const popoverRef = useRef<any>(null)
  const ref = useDetectClickOutside({
    onTriggered: () => setShowPopover(false),
    disableTouch: true,
  })

  const handleClick = () => {
    setShowPopover(!showPopover)
  }

  function formatTimeDifference(timestamp: { seconds: number; nanoseconds: number }): string {
    const now = new Date()
    const pastDate = new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1e6)

    const timeDifference = now.getTime() - pastDate.getTime()
    const secondsDifference = Math.floor(timeDifference / 1000)

    if (secondsDifference < 60) {
      return `${secondsDifference} s`
    } else if (secondsDifference < 3600) {
      const minutes = Math.floor(secondsDifference / 60)
      return `${minutes} m`
    } else if (secondsDifference < 86400) {
      const hours = Math.floor(secondsDifference / 3600)
      return `${hours} h`
    } else if (secondsDifference < 604800) {
      const days = Math.floor(secondsDifference / 86400)
      return `${days} d`
    } else if (secondsDifference < 2419200) {
      const weeks = Math.floor(secondsDifference / 604800)
      return `${weeks} w`
    } else if (now.getFullYear() === pastDate.getFullYear()) {
      const options = { month: 'long', day: 'numeric' } as const
      return pastDate.toLocaleDateString(undefined, options)
    } else {
      const options = { year: 'numeric', month: 'long', day: 'numeric' } as const
      return pastDate.toLocaleDateString(undefined, options)
    }
  }

  const calculatePopoverPosition = () => {
    const ellipsisRect = popoverRef.current.getBoundingClientRect()
    return {
      top: ellipsisRect.bottom + window.scrollY - 65,
      marginLeft: '55%',
    }
  }

  const popoverStyle: React.CSSProperties = showPopover
    ? calculatePopoverPosition()
    : { display: 'none' }

  return (
    <>
      <li className={`process ${!notification.read ? 'process-bg-read' : ''}`}>
        <div className='d-flex justify-content-between'>
          <div>
            <span className='process-title'>{notification.title}</span>
            <span className='process-started-at'>{notification.description}</span>
          </div>
          <div className='d-flex flex-column justify-content-center align-items-center notification-settings'>
            <p>{formatTimeDifference(notification.date)}</p>
            <div ref={ref} className='position: relative'>
              <i
                ref={popoverRef}
                className='fa-solid fa-ellipsis notification-3dots-icon'
                onClick={handleClick}
              />
              {showPopover && (
                <div className='notification-popover-wrapper' style={popoverStyle}>
                  <div className='notification-popover'>
                    <p onClick={() => handleMarkNotification(notification)}>
                      {t('notifications.mark_as')}Mark as {notification.read ? t('notifications.unread') : t('notifications.read')}{' '}
                    </p>
                    <p onClick={() => handleDeleteNotification(notification.id)}>Delete</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </li>
    </>
  )
}

export default observer(NotificationEntrys)
